import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["name", "scope", "role", "isDefault", "errors", "submitBtn", "title"]
  static values = { url: String, method: String }

  connect() {
    this._state = {}
    this._mode = "create"
    this._editingId = null
    this._onOpenSave = this._onOpenSave.bind(this)
    this._onOpenEdit = this._onOpenEdit.bind(this)
    document.addEventListener("portfolio-preset:open-save", this._onOpenSave)
    document.addEventListener("portfolio-preset:open-edit", this._onOpenEdit)
  }

  disconnect() {
    document.removeEventListener("portfolio-preset:open-save", this._onOpenSave)
    document.removeEventListener("portfolio-preset:open-edit", this._onOpenEdit)
  }

  _onOpenSave(event) {
    this._mode = "create"
    this._editingId = null
    this._state = event.detail || {}
    this.titleTarget.textContent = "Save current view"
    this.submitBtnTarget.textContent = "Save view"
    this.nameTarget.value = ""
    this.scopeTargets.forEach(r => { r.checked = r.value === "personal" })
    this.roleTarget.disabled = true
    this.isDefaultTarget.checked = false
    this._clearError()
  }

  _onOpenEdit(event) {
    const preset = event.detail?.preset
    if (!preset) return
    this._mode = "edit"
    this._editingId = preset.id
    this._state = preset.filters || {}
    this.titleTarget.textContent = "Edit preset"
    this.submitBtnTarget.textContent = "Save changes"
    this.nameTarget.value = preset.name || ""
    const scope = preset.scope || "personal"
    this.scopeTargets.forEach(r => { r.checked = r.value === scope })
    if (scope === "role" && preset.company_admin_role) {
      this.roleTarget.value = preset.company_admin_role
      this.roleTarget.disabled = false
    } else {
      this.roleTarget.disabled = true
    }
    this.isDefaultTarget.checked = !!preset.is_default
    this._clearError()
  }

  onScopeChange(event) {
    const val = event.target.value
    this.roleTarget.disabled = val !== "role"
  }

  async submit() {
    const name = this.nameTarget.value.trim()
    if (!name) {
      this._showError("Name is required")
      return
    }

    const scopeEl = this.scopeTargets.find(r => r.checked)
    const scope = scopeEl ? scopeEl.value : "personal"
    const role = scope === "role" ? this.roleTarget.value : null

    const body = new URLSearchParams()
    body.append("name", name)
    body.append("scope", scope)
    if (role) body.append("company_admin_role", role)
    if (this.isDefaultTarget.checked) body.append("is_default", "true")

    Object.entries(this._state).forEach(([k, v]) => {
      if (k === "cols") {
        v.split(",").filter(Boolean).forEach(col => body.append("visible_columns[]", col))
      } else if (k === "col_order") {
        v.split(",").filter(Boolean).forEach(col => body.append("column_order[]", col))
      } else {
        body.append(k, v)
      }
    })

    const url = this._mode === "edit" ? `${this.urlValue}/${this._editingId}` : this.urlValue
    const method = this._mode === "edit" ? "PATCH" : "POST"

    this._setSubmitting(true)
    try {
      const response = await fetch(url, {
        method,
        headers: {
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content,
          Accept: "application/json",
          "Content-Type": "application/x-www-form-urlencoded"
        },
        body
      })
      const data = await response.json()
      if (!response.ok || !data.success) {
        this._showError((data.errors || ["Save failed"]).join(", "))
        return
      }
      this._showToast(this._mode === "edit" ? "Preset updated" : "View saved", "notice")
      this.dispatch("close")
      this._reloadDashboardWithPreset(data.preset)
    } catch (err) {
      this._showError(err.message || "Network error")
    } finally {
      this._setSubmitting(false)
    }
  }

  _reloadDashboardWithPreset(preset) {
    const frame = document.getElementById("portfolio-dashboard-content")
    if (!frame) return
    const url = new URL(window.location.href)
    if (preset?.id && this._mode === "create") {
      url.searchParams.set("preset", preset.id)
    }
    frame.src = url.toString()
  }

  _showError(msg) {
    this.errorsTarget.textContent = msg
    this.errorsTarget.classList.remove("hidden")
  }

  _clearError() {
    this.errorsTarget.textContent = ""
    this.errorsTarget.classList.add("hidden")
  }

  _setSubmitting(isSubmitting) {
    this.submitBtnTarget.disabled = isSubmitting
    this.submitBtnTarget.textContent = isSubmitting
      ? "Saving…"
      : (this._mode === "edit" ? "Save changes" : "Save view")
  }

  _showToast(message, type = "notice") {
    const toast = document.createElement("div")
    toast.className = type === "alert" ? "flash-toast-alert" : "flash-toast-notice"
    toast.setAttribute("data-controller", "flash")
    toast.setAttribute("data-flash-dismiss-after-value", "5000")
    toast.textContent = message
    document.body.prepend(toast)
  }
}
